import { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import * as Yup from "yup";
import { debounce } from "lodash";
import { useCalculateApplication } from "@/hooks/useCalculateApplication";
import { dollarStringOptions } from "../../../constants";
import { NumericFormatCustom } from "../../../utils/currencyMaskFormat";
import regex from "../../../utils/regex";
import {
  loanDetailsSelector,
  updateLoanDetails,
  saveLoanDetails,
} from "../../../store/slices/applicationFormSlice";

const Balloon = ({ applicationId, disabled }) => {
  const dispatch = useDispatch();
  const { getBalloonPercentage, getBalloonAmount } = useCalculateApplication();

  const loanDetails = useSelector(loanDetailsSelector);
  const [mode, setMode] = useState({ balloon: "percentage" });
  const isBallonPerCheck = mode.balloon === "percentage";

  const {
    _id,
    balloon = 0,
    balloonAmount = 0,
  } = loanDetails;

  const [allValues, setAllValue] = useState({
    balloon: balloon || 0,
    balloonAmount: balloonAmount || 0,
  });

  useEffect(() => {
    setAllValue({
      balloon: balloon || 0,
      balloonAmount: balloonAmount || 0,
    })
  }, [loanDetails?.balloon])


  const [fieldErrors, setFieldErrors] = useState({
    balloon: "",
    balloonAmount: "",
  });

  const validationSchema = Yup.object({
    balloon: Yup.string()
      .optional()
      .matches(regex.percentageRegex, "Please enter valid percentage.")
      .matches(regex.percentageRegex, {
        message: "Percentage should be upto 100%.",
      }),
    balloonAmount: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be to up 7 digit and up to 3 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
  });

  const debouncedValidation = useCallback(
    debounce(async (fieldName, value) => {
      try {
        await validationSchema.validateAt(fieldName, { [fieldName]: value });
        setFieldErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
      } catch (error) {
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: error.message,
        }));
      }
    }, 300),
    []
  );

  const handleModeChange = (key, newMode) => {
    if (newMode !== null) {
      if (key === "balloon") {
        if (mode.balloon !== "percentage") {
          debouncedValidation("balloon", allValues.balloon);
        } else {
          debouncedValidation("balloonAmount", parseFloat(allValues.balloonAmount?.toFixed(2)));
        }
      }
      setMode({
        ...mode,
        [key]: newMode
      });
    }
  };

  const handle = {
    onChangeField: async (e, name) => {
      let isValid = true;

      if (e === "") {
        setAllValue({ ...allValues, [name]: e });
        setFieldErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
        return;
      }


      if (!isValid) {
        debouncedValidation(name, e);
        return;
      }

      if (name === "balloon") {
        if (mode.balloon === "percentage") {
          if (e < 0) {
            debouncedValidation(name, e);
            return;
          }
          setAllValue({ ...allValues, [name]: +e, balloonAmount: getBalloonAmount({ balloonPercentage: Number(e) }) });
          debouncedValidation(name, e);
          return
        } else {
          setAllValue({ ...allValues, [name]: getBalloonPercentage({ balloonAmount: Number(e) }), balloonAmount: Math.abs(e) });
        }
      } else {
        setAllValue({ ...allValues, [name]: e });
        debouncedValidation(name, e);
      }
    },
    loanDetailsFn: (value) => {
      if (loanDetails?.balloon !== value?.balloon && loanDetails?.balloonAmount !== value?.balloonAmount) {
        dispatch(saveLoanDetails(value));
        dispatch(updateLoanDetails({ applicationId, _id, ...value }));
      }
    },
  }

  return (
    <Fragment>
      <Stack direction="column">
        <Typography variant="caption">
          Balloon {!isNaN(allValues?.balloon) ? Math.round(allValues?.balloon * 100) / 100 : 0}% |{" "}
          {(allValues?.balloonAmount ? allValues?.balloonAmount : 0).toLocaleString(
            "en-US",
            dollarStringOptions,
          )}
        </Typography>
        <Box display="flex" alignItems="center">
          <TextField
            label="Balloon"
            name="balloon"
            disabled={disabled}
            value={isBallonPerCheck ? allValues?.balloon : allValues?.balloonAmount}
            error={isBallonPerCheck ? fieldErrors?.balloon : fieldErrors?.balloonAmount}
            helperText={isBallonPerCheck ? fieldErrors?.balloon : fieldErrors?.balloonAmount}
            onChange={(event) => {
              const value = event.target.value;
              handle.onChangeField(value || 0, "balloon");
            }}
            onBlur={() => {
              handle.loanDetailsFn({
                balloon: parseFloat(allValues.balloon?.toFixed(2)) || 0,
                balloonAmount: parseFloat(allValues.balloonAmount?.toFixed(2)) || 0,
              });
            }}
            InputProps={{
              inputComponent: NumericFormatCustom,
              inputProps: {
                prefix: "",
                isAllowedFlag: true,
                maxDigits: 7,
                maxPlaces: 2,
              },
              startAdornment: allValues?.balloonAmount > 0 && mode.balloon !== "percentage" && (
                <InputAdornment position="start">$</InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <ToggleButtonGroup
                    value={mode.balloon}
                    exclusive
                    color="primary"
                    onChange={(event, value) => handleModeChange("balloon", value)}
                    sx={{
                      mr: -1,
                      height: "36px",
                    }}
                  >
                    <ToggleButton value="percentage">%</ToggleButton>
                    <ToggleButton value="dollar">$</ToggleButton>
                  </ToggleButtonGroup>
                </InputAdornment>
              ),
            }}
            type="text"
            size="small"
            variant="filled"
            fullWidth
          />
        </Box>
      </Stack>
    </Fragment>
  )
}

export default Balloon